// B1.4: analytics domain methods (move-only from client.ts).
import { ApiClientCore, buildQueryString } from './http/core';

export class AnalyticsApi extends ApiClientCore {
  async getAnalyticsHealthOverview(): Promise<any> {
    return this.get('/api/v1/analytics/health-overview');
  }
  async getExpiredScheduledInstanceMetrics(days?: number): Promise<any> {
    return this.get(
      `/api/v1/checklists/analytics/scheduled-instances/expired-metrics${buildQueryString(
        days != null ? { days } : {}
      )}`
    );
  }
  async archiveExpiredScheduledInstances(payload: { older_than_days: number; dry_run: boolean }): Promise<any> {
    return this.post('/api/v1/checklists/analytics/scheduled-instances/archive-expired', payload);
  }
  async cancelStaleChecklist(checklistId: number, params?: { min_days?: number; reason?: string }): Promise<any> {
    return this.post(
      `/api/v1/checklists/${checklistId}/cancel-stale${buildQueryString((params || {}) as Record<string, any>)}`
    );
  }
  async getChecklistsAnalyticsSummary(params?: {
    date_from?: string;
    date_to?: string;
    location_id?: number;
    template_id?: number;
  }): Promise<{
    period: { from: string; to: string };
    total_checklists: number;
    completed_count: number;
    in_progress_count: number;
    pending_count: number;
    cancelled_count: number;
    completion_rate: number;
    overdue_count: number;
    overdue_rate: number;
    avg_completion_time_minutes: number | null;
  }> {
    return this.get(`/api/v1/checklists/analytics/summary${buildQueryString(params || {})}`);
  }
  async getChecklistsAnalyticsByTemplate(params?: {
    date_from?: string;
    date_to?: string;
    limit?: number;
  }): Promise<any[]> {
    return this.get(`/api/v1/checklists/analytics/by-template${buildQueryString(params || {})}`);
  }
  async getChecklistsAnalyticsTrends(params?: {
    date_from?: string;
    date_to?: string;
    group_by?: 'day' | 'week' | 'month';
  }): Promise<any[]> {
    return this.get(`/api/v1/checklists/analytics/trends${buildQueryString(params || {})}`);
  }
  async getConsumableAnalyticsSummary(params?: { location_id?: number; days?: number }): Promise<any> {
    return this.get(`/api/v1/consumables/analytics/summary${buildQueryString(params || {})}`);
  }
  async getConsumableWorkflowAnalyticsSummary(params?: { date_from?: string; date_to?: string }): Promise<any> {
    return this.get(`/api/v1/consumables/analytics/workflow/summary${buildQueryString(params || {})}`);
  }
  async getAnalyticsHub(params: { period?: 'today' | '7d' | '30d'; location_id?: number } = {}): Promise<any> {
    return this.get(`/api/v1/analytics/hub${buildQueryString(params)}`);
  }
  async getAnalyticsOverview(params: { days?: number; location_id?: number } = {}): Promise<any> {
    return this.get(`/api/v1/analytics/overview${buildQueryString(params)}`);
  }
  async getAnalyticsRevenueTrend(days: number, locationId?: number): Promise<any> {
    const params: Record<string, any> = { days };
    if (locationId !== undefined) params.location_id = locationId;
    return this.get(`/api/v1/analytics/charts/revenue-trend${buildQueryString(params)}`);
  }
  async getAnalyticsIncidentsTrend(days: number, locationId?: number): Promise<any> {
    const params: Record<string, any> = { days };
    if (locationId !== undefined) params.location_id = locationId;
    return this.get(`/api/v1/analytics/charts/incidents-trend${buildQueryString(params)}`);
  }
  async getAnalyticsChecklistsTrend(days: number, locationId?: number): Promise<any> {
    const params: Record<string, any> = { days };
    if (locationId !== undefined) params.location_id = locationId;
    return this.get(`/api/v1/analytics/charts/checklists-trend${buildQueryString(params)}`);
  }
  async getAnalyticsPayrollReport(days: number): Promise<any> {
    return this.get(`/api/v1/analytics/reports/payroll${buildQueryString({ days })}`);
  }
  async getAnalyticsConsumablesReport(days: number, locationId?: number): Promise<any> {
    const params: Record<string, any> = { days };
    if (locationId !== undefined) params.location_id = locationId;
    return this.get(`/api/v1/analytics/reports/consumables${buildQueryString(params)}`);
  }
  async getAnalyticsEmployeeReports(days: number, locationId?: number): Promise<any> {
    const params: Record<string, any> = { days };
    if (locationId !== undefined) params.location_id = locationId;
    return this.get(`/api/v1/analytics/reports/employees${buildQueryString(params)}`);
  }
  async getChecklistsAnalyticsByEmployee(params?: {
    date_from?: string;
    date_to?: string;
    limit?: number;
  }): Promise<any[]> {
    return this.get(`/api/v1/checklists/analytics/by-employee${buildQueryString(params || {})}`);
  }
}
